import { useCallback, useState } from 'react';
import { useLazyGetCepDataQuery } from '../services/cepApi';
export function useAddressZipCodeData(currentZipCode) {
    const [trigger] = useLazyGetCepDataQuery();
    const [isZipCodeLoading, setIsZipCodeLoading] = useState(false);
    const [zipCodeNotFoundError, setZipCodeNotFoundError] = useState(false);
    const [zipCodeApiError, setZipCodeApiError] = useState(false);
    const [zipCodeAddressData, setZipCodeAddressData] = useState(null);
    const [lastSearchedZipCode, setLastSearchedZipCode] = useState('');
    const getZipCodeNumbers = useCallback((zipCode) => {
        return zipCode.replace(/\D/g, '');
    }, []);
    const isZipCodeComplete = useCallback((zipCode) => {
        const numbers = getZipCodeNumbers(zipCode);
        return numbers.length === 8;
    }, [getZipCodeNumbers]);
    const searchZipCode = useCallback(async (zipCode) => {
        const zipCodeNumbers = getZipCodeNumbers(zipCode);
        if (!isZipCodeComplete(zipCode)) {
            setZipCodeNotFoundError(false);
            setZipCodeApiError(false);
            setIsZipCodeLoading(false);
            setZipCodeAddressData(null);
            return;
        }
        if (zipCodeNumbers === lastSearchedZipCode && zipCodeAddressData) {
            return;
        }
        setIsZipCodeLoading(true);
        setZipCodeNotFoundError(false);
        setZipCodeApiError(false);
        setZipCodeAddressData(null);
        try {
            const result = await trigger({ cep: zipCodeNumbers });
            if (result.error) {
                setZipCodeApiError(true);
                setZipCodeNotFoundError(false);
                setLastSearchedZipCode('');
                setZipCodeAddressData(null);
            }
            else if (result.data?.erro || !result.data) {
                setZipCodeNotFoundError(true);
                setZipCodeApiError(false);
                setLastSearchedZipCode('');
                setZipCodeAddressData(null);
            }
            else {
                const data = result.data;
                setLastSearchedZipCode(zipCodeNumbers);
                setZipCodeNotFoundError(false);
                setZipCodeApiError(false);
                setZipCodeAddressData({
                    street: data.logradouro || '',
                    neighborhood: data.bairro || '',
                    city: data.localidade || '',
                    state: data.uf || '',
                });
            }
        }
        catch {
            setZipCodeApiError(true);
            setZipCodeNotFoundError(false);
            setLastSearchedZipCode('');
            setZipCodeAddressData(null);
        }
        finally {
            setIsZipCodeLoading(false);
        }
    }, [trigger, isZipCodeComplete, getZipCodeNumbers, lastSearchedZipCode, zipCodeAddressData]);
    const clearZipCodeData = useCallback(() => {
        setZipCodeNotFoundError(false);
        setZipCodeApiError(false);
        setZipCodeAddressData(null);
        setLastSearchedZipCode('');
    }, []);
    const handleZipCodeChange = useCallback((zipCode) => {
        const zipCodeNumbers = getZipCodeNumbers(zipCode);
        if (zipCodeNumbers.length < 8) {
            clearZipCodeData();
            return;
        }
        searchZipCode(zipCode);
    }, [searchZipCode, getZipCodeNumbers, clearZipCodeData]);
    const isCurrentZipCodeComplete = isZipCodeComplete(currentZipCode ?? '');
    const shouldDisableFields = !isCurrentZipCodeComplete || isZipCodeLoading;
    return {
        isZipCodeLoading,
        zipCodeNotFoundError,
        zipCodeApiError,
        shouldDisableFields,
        zipCodeAddressData,
        handleZipCodeChange,
        clearZipCodeData,
    };
}
